import { z } from 'zod';
/**
 * The unified agent -> server feed protocol.
 *
 * Zod schemas are the source of truth; TypeScript types are inferred from them.
 * The agent validates before sending, the server validates on ingest, and the
 * replayer validates when loading a recording — so a malformed event can never
 * reach the market engine.
 *
 * Forward compatibility rule: unknown Live Client event names arrive as
 * `kind: 'unknown'` with `rawName` preserved rather than being dropped. Riot adds
 * objectives (Horde, Atakhan) without warning; the book must survive that.
 */
export const PROTOCOL_VERSION = 1;
// ── shared value objects ────────────────────────────────────────────────────
/** ORDER = blue side, CHAOS = red side (Live Client Data API vocabulary). */
export const TeamSchema = z.enum(['ORDER', 'CHAOS']);
export const PositionSchema = z.enum([
    'TOP',
    'JUNGLE',
    'MIDDLE',
    'BOTTOM',
    'UTILITY',
    'NONE',
    'UNKNOWN',
]);
export const ScoresSchema = z.object({
    kills: z.number().int().nonnegative(),
    deaths: z.number().int().nonnegative(),
    assists: z.number().int().nonnegative(),
    /** NOTE: Riot bug — creepScore only updates in increments of 10. */
    creepScore: z.number().int().nonnegative(),
    wardScore: z.number().nonnegative(),
});
export const PlayerSnapshotSchema = z.object({
    /** "GameName#TAG" — the identity key post Riot-ID migration. */
    riotId: z.string().min(1),
    gameName: z.string(),
    tagLine: z.string(),
    championName: z.string(),
    team: TeamSchema,
    position: PositionSchema,
    level: z.number().int().nonnegative(),
    isDead: z.boolean(),
    respawnTimer: z.number().nonnegative(),
    isBot: z.boolean(),
    scores: ScoresSchema,
    /** Item ids only — we never need names server-side. */
    items: z.array(z.number().int()),
    /** True for the account the agent is running on (her). */
    isSelf: z.boolean(),
});
export const ChampSelectPlayerSchema = z.object({
    cellId: z.number().int(),
    championId: z.number().int(),
    /** Pick intent before lock-in; 0 when none. */
    championPickIntent: z.number().int(),
    assignedPosition: z.string(),
    isSelf: z.boolean(),
});
// ── session lifecycle ───────────────────────────────────────────────────────
export const AgentHelloSchema = z.object({
    type: z.literal('agent_hello'),
    agentVersion: z.string(),
    /** Free-text label for which machine this is (dev box vs her PC). */
    machine: z.string(),
    protocolVersion: z.literal(PROTOCOL_VERSION),
    /** Set when this session is a replay of a recording, never for live play. */
    replayOf: z.string().nullable().default(null),
    /** Highest seq the agent believes the server already has (reconnect). */
    resumeFromSeq: z.number().int().nonnegative().nullable().default(null),
});
export const AgentStatusSchema = z.object({
    type: z.literal('agent_status'),
    lcu: z.boolean(),
    liveClient: z.boolean(),
    onAir: z.boolean(),
    accountKey: z.string().nullable(),
});
export const AgentOfflineSchema = z.object({
    type: z.literal('agent_offline'),
    reason: z.enum(['toggle_off', 'shutdown']),
});
// ── account / lobby (LCU) ───────────────────────────────────────────────────
export const AccountActiveSchema = z.object({
    type: z.literal('account_active'),
    puuid: z.string().min(1),
    riotId: z.string().min(1),
    gameName: z.string(),
    tagLine: z.string(),
    /** Our internal label from agent config; null if this account isn't configured. */
    accountKey: z.string().nullable(),
    summonerLevel: z.number().int().nonnegative().nullable().default(null),
});
export const QueueEnteredSchema = z.object({
    type: z.literal('queue_entered'),
    queueId: z.number().int(),
    queueName: z.string(),
    ranked: z.boolean(),
});
export const QueueLeftSchema = z.object({
    type: z.literal('queue_left'),
    reason: z.enum(['cancelled', 'match_found', 'unknown']),
});
export const ReadyCheckSchema = z.object({
    type: z.literal('ready_check'),
    state: z.string(),
    response: z.enum(['None', 'Accepted', 'Declined']),
});
export const ChampSelectUpdateSchema = z.object({
    type: z.literal('champ_select_update'),
    /** LCU timer phase: BAN_PICK, FINALIZATION, ... */
    phase: z.string(),
    timerMs: z.number().int().nonnegative(),
    localPlayerCellId: z.number().int(),
    /**
     * Ally cells only. Ranked champ select obfuscates teammate identity
     * (patch 12.22+) and Riot policy forbids revealing it — champions and
     * positions are all we take, all we need, and all we may use.
     */
    myTeam: z.array(ChampSelectPlayerSchema),
    bans: z.object({
        ours: z.array(z.number().int()),
        theirs: z.array(z.number().int()),
    }),
});
export const ChampSelectAbortedSchema = z.object({
    type: z.literal('champ_select_aborted'),
    reason: z.enum(['dodge_self', 'dodge_other', 'unknown']),
    /** Raw LCU gameDodge.state when we managed to read it (it is transient). */
    dodgeState: z.string().nullable().default(null),
});
export const GameStartingSchema = z.object({
    type: z.literal('game_starting'),
    queueId: z.number().int().nullable(),
    gameId: z.number().int().nullable(),
});
export const MatchIdentifiedSchema = z.object({
    type: z.literal('match_identified'),
    gameId: z.number().int(),
    platformId: z.string(),
    /** `${platformId}_${gameId}` — the match-v5 id. */
    matchId: z.string(),
});
// ── live game (Live Client Data API) ────────────────────────────────────────
export const GameSnapshotSchema = z.object({
    type: z.literal('game_snapshot'),
    /** Seconds since game start, float. */
    gameTime: z.number().nonnegative(),
    gameMode: z.string(),
    mapName: z.string(),
    /** False for modes we do not book (ARAM, Arena, ...) — recorded, not priced. */
    bookable: z.boolean(),
    players: z.array(PlayerSnapshotSchema),
    activePlayer: z.object({
        riotId: z.string(),
        level: z.number().int().nonnegative(),
        currentGold: z.number().nonnegative(),
    }).nullable(),
});
export const GameEventKindSchema = z.enum([
    'game_start',
    'minions_spawning',
    'first_blood',
    'first_brick',
    'champion_kill',
    'multikill',
    'ace',
    'turret_killed',
    'inhib_killed',
    'inhib_respawning',
    'inhib_respawned',
    'dragon_kill',
    'herald_kill',
    'baron_kill',
    'horde_kill',
    'atakhan_kill',
    'game_end',
    'unknown',
]);
export const GameEventSchema = z.object({
    type: z.literal('game_event'),
    /** Live Client EventID — sequential per game, resets each game. */
    eventId: z.number().int().nonnegative(),
    kind: GameEventKindSchema,
    /** Original Riot EventName, always preserved (forward compatibility). */
    rawName: z.string(),
    gameTime: z.number().nonnegative(),
    killer: z.string().nullable().default(null),
    victim: z.string().nullable().default(null),
    assisters: z.array(z.string()).default([]),
    /** Fire/Earth/Water/Air/Hextech/Chemtech/Elder */
    dragonType: z.string().nullable().default(null),
    /** Riot sends the string "True"/"False"; normalized to a real boolean here. */
    stolen: z.boolean().nullable().default(null),
    /** Turret/inhibitor identifier, e.g. Turret_T2_L_03_A */
    structure: z.string().nullable().default(null),
    /** Derived side for structure/objective events where we can infer it. */
    team: TeamSchema.nullable().default(null),
    killStreak: z.number().int().nullable().default(null),
    /** GameEnd only: result relative to the local player. */
    result: z.enum(['win', 'loss']).nullable().default(null),
});
export const GameEndedSchema = z.object({
    type: z.literal('game_ended'),
    result: z.enum(['win', 'loss', 'unknown']),
    /**
     * Which signal we actually observed. A client crash can skip GameEnd
     * entirely, so settlement must know whether the result is trustworthy.
     */
    endSignal: z.enum(['game_end_event', 'process_exit']),
    gameTime: z.number().nonnegative().nullable().default(null),
    /** From the LCU end-of-game block when available. */
    earlySurrender: z.boolean().nullable().default(null),
});
export const GameAbortedSchema = z.object({
    type: z.literal('game_aborted'),
    reason: z.enum(['remake', 'unknown']),
});
// ── the union ───────────────────────────────────────────────────────────────
export const FeedEventSchema = z.discriminatedUnion('type', [
    AgentHelloSchema,
    AgentStatusSchema,
    AgentOfflineSchema,
    AccountActiveSchema,
    QueueEnteredSchema,
    QueueLeftSchema,
    ReadyCheckSchema,
    ChampSelectUpdateSchema,
    ChampSelectAbortedSchema,
    GameStartingSchema,
    MatchIdentifiedSchema,
    GameSnapshotSchema,
    GameEventSchema,
    GameEndedSchema,
    GameAbortedSchema,
]);
/** Every event type, for exhaustiveness tests and admin tooling. */
export const FEED_EVENT_TYPES = [
    'agent_hello',
    'agent_status',
    'agent_offline',
    'account_active',
    'queue_entered',
    'queue_left',
    'ready_check',
    'champ_select_update',
    'champ_select_aborted',
    'game_starting',
    'match_identified',
    'game_snapshot',
    'game_event',
    'game_ended',
    'game_aborted',
];
/** Compile-time guarantee that a switch covered every case. */
export function assertNever(x, context = 'unhandled case') {
    throw new Error(`${context}: ${JSON.stringify(x)}`);
}
//# sourceMappingURL=events.js.map